import React, { useRef, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import Tooltip from '@material-ui/core/Tooltip';

import CommonImage from '@components/CommonImage';
import InputWithPrefix from '@components/InputWithPrefix';
import RfqChangeHistory from '@components/RfqChangeHistory';

import AddColorModal from '@features/order/components/orderInfo/AddColorModal';
import AddSizeModal from '@features/order/components/orderInfo/AddSizeModal';
import { incoTerms } from '@features/order/utils/projectUtil';
import EditColorModal from '@features/order/components/orderInfo/EditColorModal';

import { fetchSizeForProducts } from '@store/design/action';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import { fetchEnumValues } from '@services/commonService';
import { fetchAllSizeCategory, fetchSizeCategoryById } from '@services/measurementService';

import { getShortName, parseQuoteName, printApiError } from '@utils/Util';

import { toastError } from '@/components/Toast';
import CustomDropdown from '@/components/CustomDropdown';

const ColorSizeBreakDownTable = ({
	item,
	stylesData,
	setOrderStyles,
	quoteId,
	isDrafted,
	error,
	errorDesign,
	setIsOverflow,
	poItemIdentifier,
	isDisabled,
	pOSegmentationType,
	defaultPriceType,
	isFromPoAutomation = false,
	dropDownselectedProductId = null,
	selectedSizeCategory = null,
	handleSearch
}) => {
	const [isAddSize, setIsAddSize] = useState(false);
	const [, setIsAddColor] = useState(false);
	const [additionalInfo, setAdditionalInfo] = useState([]);
	const [addDesignIndex, setAddDesignIndex] = useState('');
	const [removedColorIds, setRemovedColorIds] = useState([]);
	const [editColorPopUp, setEditColorPopUp] = useState(false);
	const [directColorPopUp, setDirectColorPopUp] = useState(false);
	const [selectedColorEditList, setSelectedColorEditList] = useState([]);
	const [selectedColorEditName, setSelectedColorEditName] = useState('');
	const [colorType, setColorType] = useState('');
	const [directEditColor, setDirectEditColor] = useState(null);
	const [localSegmentationTypes, setLocalSegmentationTypes] = useState([]);
	const [sizeCategoryList, setSizeCategoryList] = useState([]);
	const [standardSizeList, setStandardSizeList] = useState([]);
	const [, setSelectedSizeCategoryId] = useState(null);
	const [localSelectedSizeCategory, setSelectedSizeCategory] = useState(selectedSizeCategory);
	const [isSizeDropdownOpen, setIsSizeDropdownOpen] = useState(false);
	const sizeRef = useRef(null);
	//let prevDataRef = useRef(null);
	const dispatch = useDispatch();

	const segmentationTypes = pOSegmentationType?.length ? pOSegmentationType : localSegmentationTypes;

	useEffect(() => {
		if (!pOSegmentationType?.length) {
			fetchEnumValues('info.nitex.app.rfq.POSegmentationType', setLocalSegmentationTypes);
		}
	}, [pOSegmentationType]);

	useEffect(() => {
		if (!item?.productId || !isFinite(Number(item.productId))) return;
		(async () => {
			try {
				dispatch(openLoader());
				const sizeCategory = await fetchAllSizeCategory(`?productId=${item.productId}`);
				const mappedSizeCategoryList =
					sizeCategory?.map((item) => ({ id: item.id, value: item.name })) || [];
				setSizeCategoryList(mappedSizeCategoryList);
				// use design's own sizeCategoryId so standardSizeList matches what SelectSizeCategory uses
				const sizeCategoryIdToFetch = item?.sizeCategoryId || sizeCategory?.[0]?.id;
				if (sizeCategoryIdToFetch) {
					const data = await fetchSizeCategoryById(sizeCategoryIdToFetch);
					setStandardSizeList(data?.productSizeList || []);
					// pre-select the dropdown item matching the design's own sizeCategoryId
					if (item?.sizeCategoryId) {
						const matchedSizeCategory = mappedSizeCategoryList.find(
							(cat) => cat.id === item.sizeCategoryId
						);
						setSelectedSizeCategoryId(item.sizeCategoryId);
						setSelectedSizeCategory(matchedSizeCategory?.value || data?.name || '');
					}
				}
			} catch {
				dispatch(closeLoader());
			} finally {
				dispatch(closeLoader());
			}
		})();
	}, [item?.productId]);

	// replaces one size column in stylesData with the selected size from standardSizeList
	const handleSizeColumnUpdate = (selectedSize, currentSizeId) => {
		const newData = [...stylesData];
		const updatedData = newData.map((style) => {
			if (style.id !== quoteId) return style;

			// migrate sizeWiseBuyerPrice key from old id to new id
			const updatedSizeWiseBuyerPrice = { ...(style.sizeWiseBuyerPrice || {}) };
			if (updatedSizeWiseBuyerPrice[currentSizeId] !== undefined) {
				updatedSizeWiseBuyerPrice[selectedSize.id] = updatedSizeWiseBuyerPrice[currentSizeId];
				delete updatedSizeWiseBuyerPrice[currentSizeId];
			}

			return {
				...style,
				sizeWiseBuyerPrice: updatedSizeWiseBuyerPrice,
				colorWiseSizeQuantityPairList: style.colorWiseSizeQuantityPairList.map((colorItem) => ({
					...colorItem,
					sizeQuantityPairList: colorItem.sizeQuantityPairList.map((sizeItem) =>
						sizeItem.id === currentSizeId
							? { ...sizeItem, id: selectedSize.id, label: selectedSize.value }
							: sizeItem
					)
				}))
			};
		});
		setOrderStyles(updatedData);
	};

	const onItemClick = async (item) => {
		if (item?.title === 'Size Category') {
			try {
				dispatch(openLoader());
				setSelectedSizeCategoryId(item.id);
				setSelectedSizeCategory(item.value);
				const data = await fetchSizeCategoryById(item.id);
				setStandardSizeList(data.productSizeList);
				setSelectedSizeCategory(data.name);
				setSelectedSizeCategoryId(item.id);
				dispatch(closeLoader());
			} catch {
				dispatch(closeLoader());
			}
		}
	};

	// let allQuotation = useSelector(getEnumFilter);
	const onAddSizeModal = (id) => {
		const newData = [...stylesData];

		const design = newData.find((ite) => ite.id === id);

		dispatch(fetchSizeForProducts(design?.productId));
		setIsAddSize(true);
		setAddDesignIndex(id);
	};
	const onAddMissingSizeModal = (id, currentQuoteId) => {
		const isIdNumber = isFinite(Number(id));
		if (isFromPoAutomation && !isIdNumber) {
			toastError('Please select a product before adding size.');
			return;
		}
		const newData = [...stylesData];
		const design =
			newData.find((ite) => ite.id === currentQuoteId) ||
			newData.find((ite) => ite.productId === Number(id));
		if (!design) return;

		dispatch(fetchSizeForProducts(design?.productId));
		setIsAddSize(true);
		setAddDesignIndex(design?.id);
	};

	const onChangePriceType = (type, quoteId) => {
		const newData = [...stylesData];
		const selectedDesign = newData.find((ite) => ite.id === quoteId);
		selectedDesign['priceType'] = type;
		setOrderStyles(newData);
	};

	const sortBySerial = (arrayList, sizeCategorySequence) => {
		// Validate inputs
		if (!Array.isArray(arrayList)) {
			return [];
		}

		const sequenceMap =
			sizeCategorySequence && typeof sizeCategorySequence === 'object' ? sizeCategorySequence : {};

		let modifiedArrayList = arrayList.map((item) => {
			if (
				item &&
				typeof item === 'object' &&
				item.sizeCategoryId &&
				item.sizeCategoryId in sequenceMap
			) {
				item.sizeCategorySerial = sequenceMap[item.sizeCategoryId];
			}
			return item;
		});
		return modifiedArrayList.slice().sort((a, b) => {
			const serialA = (a && a.sizeCategorySerial) || Number.POSITIVE_INFINITY;
			const serialB = (b && b.sizeCategorySerial) || Number.POSITIVE_INFINITY;
			if (serialA !== serialB) {
				return serialA - serialB;
			}

			// If sizeCategorySerial is the same, compare by sizeCategoryId
			if ((a && a.sizeCategoryId) !== (b && b.sizeCategoryId)) {
				return (a?.sizeCategoryId || 0) - (b?.sizeCategoryId || 0);
			}

			// If both sizeCategorySerial and sizeCategoryId are the same, compare by order
			return (a?.order || 0) - (b?.order || 0);
		});
	};

	const onAddColorModal = (index) => {
		setAddDesignIndex(index);
		setIsAddColor(true);
	};

	const indvisualEditColor = (color) => {
		setDirectEditColor(color);
		setColorType(color.colorType);
		setSelectedColorEditName(color.name);
		// only pre-select a color when it actually holds real data, not a blank automation placeholder
		if (color.colorType === 'SOLID' && color.hexCode) {
			setSelectedColorEditList([color]);
		} else if (color.colorType !== 'SOLID' && color.compositeColorList?.length) {
			setSelectedColorEditList(color.compositeColorList);
		} else {
			setSelectedColorEditList([]);
		}
		setDirectColorPopUp(true);
		setIsOverflow(true);
	};

	const handleColorUpdate = (updatedColor) => {
		const newData = [...stylesData];
		const orderData = newData.find((ite) => ite.id === quoteId);
		if (orderData && directEditColor) {
			const idx = orderData.colorWiseSizeQuantityPairList.findIndex((c) => c.id === directEditColor.id);
			if (idx !== -1) {
				orderData.colorWiseSizeQuantityPairList[idx] = {
					...orderData.colorWiseSizeQuantityPairList[idx],
					id: updatedColor.id,
					name: updatedColor.name,
					colorName: updatedColor.name,
					colorType: updatedColor.colorType,
					hexCode: updatedColor.hexCode,
					compositeColorList: updatedColor.compositeColorList || [],
					selectedPantoneColorId: updatedColor.id
				};
				setOrderStyles(newData);
			}
		}
		setDirectColorPopUp(false);
		if (setIsOverflow) setIsOverflow(false);
	};

	const onChangeQuotationType = (item, quoteId) => {
		const newData = [...stylesData];
		// const selectedDesign = newData[quoteId];
		const selectedDesign = newData.find((ite) => ite.id === quoteId);
		selectedDesign['buyerQuotationType'] = item.value;
		setOrderStyles(newData);
	};

	const onChangeSKUType = (item, quoteId) => {
		const newData = [...stylesData];
		const selectedDesign = newData.find((ite) => ite.id === quoteId);
		selectedDesign['poItemIdentifierType'] = item.value;
		setOrderStyles(newData);
	};

	const openAdditionlInfo = (colorItem) => {
		setAdditionalInfo((prev) => {
			const existingItem = prev.find((info) => info.id === colorItem.id);

			if (existingItem) {
				// If the item is already in the array, toggle its isOpen status
				return prev.map((info) =>
					info.id === colorItem.id ? { ...info, isOpen: !info.isOpen } : info
				);
			} else {
				// If the item is not in the array, add it with isOpen set to true
				return [...prev, { id: colorItem.id, isOpen: true }];
			}
		});
	};
	const isOpenAdditionalInfo = (colorItem) => {
		if (!Array.isArray(additionalInfo)) {
			return false;
		}

		const test = additionalInfo.find((item) => item.isOpen && item.id === colorItem.id);
		return !!test;
	};

	const getTotal = (items) => {
		return items.reduce((total, pair) => {
			if (pair.quantity) {
				return total + parseInt(pair.quantity);
			}
			return total;
		}, 0);
	};
	const getItemTotal = (items = []) => {
		return items.reduce((total, item) => {
			return total + getTotal(item.sizeQuantityPairList);
		}, 0);
	};
	const calculateTotalAmount = (colorWiseSizeQuantityPairList, sizeWiseBuyerPrice) => {
		let totalAmount = 0;

		colorWiseSizeQuantityPairList.forEach((colorItem) => {
			colorItem.sizeQuantityPairList.forEach((sizeItem) => {
				if (sizeWiseBuyerPrice[sizeItem.id]) {
					totalAmount += sizeItem.quantity * sizeWiseBuyerPrice[sizeItem.id];
				}
			});
		});

		return totalAmount;
	};
	const onEditChange = (e, colorItemId, quoteId, styleId) => {
		let { value } = e.target;
		const newData = [...stylesData];

		const orderData = newData.find((ite) => ite.id === quoteId);
		if (!orderData) {
			printApiError('Order data not found for quoteId:', quoteId);
			return;
		}

		const colorItem = orderData?.colorWiseSizeQuantityPairList?.find((color) => color.id === colorItemId);
		if (!colorItem) {
			printApiError('Color item not found for colorItemId:', colorItemId);
			return;
		}

		const sizeItem = colorItem?.sizeQuantityPairList?.find((size) => size.id === styleId);
		if (!sizeItem) {
			printApiError('Size item not found for styleId:', styleId);
			return;
		}

		sizeItem.quantity = isNaN(parseInt(value)) ? 0 : value === '' ? 0 : parseInt(value);

		orderData.total = getItemTotal(orderData?.colorWiseSizeQuantityPairList);
		if (orderData?.buyerQuotationType === 'SIZEWISE') {
			orderData.amount = calculateTotalAmount(
				orderData?.colorWiseSizeQuantityPairList,
				orderData?.sizeWiseBuyerPrice
			);
		}
		if (orderData?.buyerQuotationType === 'COLORWISE') {
			orderData.amount = colorWiseCalculateTotalAmount(
				orderData?.colorWiseSizeQuantityPairList,
				orderData?.colorWiseBuyerPrice
			);
		}
		if (orderData?.buyerQuotationType === 'DESIGNWISE') {
			orderData.amount =
				getItemTotal(orderData?.colorWiseSizeQuantityPairList) * orderData.designWiseBuyerPrice;
		}

		setOrderStyles(newData);
	};
	const onChangeDesignWise = (e, quoteId) => {
		const { value } = e.target; // Extract the input value
		const newData = [...stylesData]; // Create a copy of the existing data
		const selectedOrder = newData.find((order) => order.id === quoteId);
		if (!selectedOrder) return;

		const isLocalDuplicateRow = String(selectedOrder?.id || '').startsWith('local_dup_');

		const updatedData = newData.map((order) => {
			const shouldUpdate = isLocalDuplicateRow
				? order.id === quoteId
				: order.productId === selectedOrder.productId;

			if (shouldUpdate) {
				// Update the relevant fields and calculate the amount
				const updatedDesignWisePrice = parseFloat(value);
				const updatedAmount =
					getItemTotal(order.colorWiseSizeQuantityPairList) * updatedDesignWisePrice;

				return {
					...order,
					sizeWiseBuyerPrice: {}, // Reset size-wise price
					colorWiseBuyerPrice: {}, // Reset color-wise price
					designWiseBuyerPrice: updatedDesignWisePrice, // Update design-wise price
					amount: updatedAmount || 0 // Set the calculated amount
				};
			}
			return order; // Return unchanged order for other items
		});
		setOrderStyles(updatedData);
	};

	const onChangeSizeWise = (e, quoteId, sizeId) => {
		let { value } = e.target;
		const newData = [...stylesData];
		const updatedData = newData.map((order) => {
			// if (order.productId === item.productId) {
			if (order.id === quoteId) {
				const updatedPrice = parseFloat(value);
				const tempSizeWiseBuyerPrice = {
					...order.sizeWiseBuyerPrice,
					[sizeId]: updatedPrice
				};
				const updatedAmount = calculateTotalAmount(
					order?.colorWiseSizeQuantityPairList,
					tempSizeWiseBuyerPrice
				);
				return {
					...order,
					sizeWiseBuyerPrice: {
						...order.sizeWiseBuyerPrice,
						[sizeId]: updatedPrice
					},
					colorWiseBuyerPrice: {},
					designWiseBuyerPrice: null,
					amount: updatedAmount || 0
				};
			}
			return order;
		});

		setOrderStyles(updatedData);
	};
	const colorWiseCalculateTotalAmount = (colorWiseSizeQuantityPairList, colorWiseBuyerPrice) => {
		let totalAmount = 0;

		colorWiseSizeQuantityPairList.forEach((colorItem) => {
			if (colorWiseBuyerPrice[colorItem.id]) {
				const price = colorWiseBuyerPrice[colorItem.id];
				colorItem.sizeQuantityPairList.forEach((sizeItem) => {
					totalAmount += sizeItem.quantity * price;
				});
			}
		});

		return totalAmount;
	};

	const getComputedItemAmount = (orderItem) => {
		if (!orderItem) return 0;

		if (orderItem?.buyerQuotationType === 'SIZEWISE') {
			return calculateTotalAmount(
				orderItem?.colorWiseSizeQuantityPairList || [],
				orderItem?.sizeWiseBuyerPrice || {}
			);
		}

		if (orderItem?.buyerQuotationType === 'COLORWISE') {
			return colorWiseCalculateTotalAmount(
				orderItem?.colorWiseSizeQuantityPairList || [],
				orderItem?.colorWiseBuyerPrice || {}
			);
		}

		if (orderItem?.buyerQuotationType === 'DESIGNWISE') {
			const unitPrice = Number(orderItem?.designWiseBuyerPrice);
			const totalQty = getItemTotal(orderItem?.colorWiseSizeQuantityPairList || []);
			const validUnitPrice = Number.isFinite(unitPrice) ? unitPrice : 0;
			return totalQty * validUnitPrice;
		}

		return 0;
	};

	const getDisplayItemAmount = (orderItem) => {
		const rawAmount = orderItem?.amount;
		const hasSavedAmount = rawAmount !== null && rawAmount !== undefined && rawAmount !== '';
		if (hasSavedAmount) {
			const savedAmount = Number(rawAmount);
			if (Number.isFinite(savedAmount)) return savedAmount;
		}
		return getComputedItemAmount(orderItem);
	};
	const onChangeColorWise = (e, quoteId, colorItemId) => {
		let { value } = e.target;
		const newData = [...stylesData];

		const updatedData = newData.map((order) => {
			// if (order.productId === item.productId)
			if (order.id === quoteId) {
				const updatedPrice = parseFloat(value);
				const tempColorWiseBuyerPrice = {
					...order.colorWiseBuyerPrice,
					[colorItemId]: updatedPrice
				};
				const updatedAmount = colorWiseCalculateTotalAmount(
					order.colorWiseSizeQuantityPairList,
					tempColorWiseBuyerPrice
				);
				return {
					...order,
					colorWiseBuyerPrice: {
						...order.colorWiseBuyerPrice,
						[colorItemId]: updatedPrice
					},
					sizeWiseBuyerPrice: {},
					designWiseBuyerPrice: null,
					amount: updatedAmount || 0
				};
			}
			return order;
		});

		setOrderStyles(updatedData);
	};
	const onChangePoItemIdentifierDesignWise = (e, quoteId) => {
		let { value } = e.target;
		const newData = [...stylesData];
		const orderData = newData.find((ite) => ite.id === quoteId);
		// const orderData = newData[quoteId];
		orderData['designWisePoItemIdentifier'] = value;
		orderData['sizeWisePoItemIdentifier'] = {};
		orderData['colorWisePoItemIdentifier'] = {};
		const index = newData.findIndex((ite) => ite.id === quoteId);
		if (index !== -1) {
			newData[index] = orderData;
			setOrderStyles(newData);
		} else {
			printApiError('Order data index not found');
		}
	};
	const onChangePoItemIdentifierSizeWise = (e, quoteId, sizeId) => {
		let { value } = e.target;
		const newData = [...stylesData];
		const orderData = newData.find((ite) => ite.id === quoteId);
		orderData['sizeWisePoItemIdentifier'] = {
			...orderData['sizeWisePoItemIdentifier'],
			[sizeId]: value
		};
		orderData['colorWisePoItemIdentifier'] = {};
		orderData['designWisePoItemIdentifier'] = null;
		const index = newData.findIndex((ite) => ite.id === quoteId);
		if (index !== -1) {
			newData[index] = orderData;
			setOrderStyles(newData);
			//	setSelectedColorList([...selectedColorList, item]);
		} else {
			printApiError('Order data index not found');
		}
	};
	const onChangePoItemIdentifierColorWise = (e, quoteId, colorItemId) => {
		let { value } = e.target;
		const newData = [...stylesData];
		const orderData = newData.find((ite) => ite.id === quoteId);
		orderData['colorWisePoItemIdentifier'] = {
			...orderData['colorWisePoItemIdentifier'],
			[colorItemId]: value
		};
		orderData['sizeWisePoItemIdentifier'] = {};
		orderData['designWisePoItemIdentifier'] = null;
		const index = newData.findIndex((ite) => ite.id === quoteId);
		if (index !== -1) {
			newData[index] = orderData;

			setOrderStyles(newData);
			//	setSelectedColorList([...selectedColorList, item]);
		} else {
			printApiError('Order data index not found');
		}
	};
	const getColorWisePrice = (colorWisePrices, id) => {
		if (colorWisePrices && colorWisePrices[id]) {
			return colorWisePrices[id];
		}
		return '';
	};

	/** True when every size cell in this color row has no quantity (user can remove the row). */
	const isColorWiseRowAllQuantitiesEmpty = (colorItem) => {
		if (!colorItem?.sizeQuantityPairList?.length) return true;
		return colorItem.sizeQuantityPairList.every((sq) => {
			const q = sq.quantity;
			return q === null || q === undefined || q === '' || Number(q) === 0;
		});
	};

	const removeSizeColumn = async (item, sizeId, quoteId) => {
		// Check if the current item's `id` matches the `quoteId`
		if (item.id === quoteId) {
			const updatedColorWiseList = item.colorWiseSizeQuantityPairList.map((colorItem) => {
				return {
					...colorItem,
					sizeQuantityPairList: colorItem.sizeQuantityPairList.filter(
						(sizeItem) => sizeItem.id !== sizeId
					)
				};
			});

			const updatedItem = {
				...item,
				colorWiseSizeQuantityPairList: updatedColorWiseList
			};

			const updatedData = stylesData.map((style) => (style.id === quoteId ? updatedItem : style));

			setOrderStyles(updatedData);
		}
	};
	const onRemoveColor = async (itemId, item, quoteId) => {
		if (item.id === quoteId) {
			const updatedColorList = item.colorWiseSizeQuantityPairList.filter(
				(colorItem) => colorItem.id !== itemId
			);

			const updatedColorWisePrice = { ...(item.colorWiseBuyerPrice || {}) };
			delete updatedColorWisePrice[itemId];
			const updatedItem = {
				...item,
				colorWiseSizeQuantityPairList: updatedColorList,
				colorWiseBuyerPrice: updatedColorWisePrice
			};

			const updatedData = stylesData.map((style) => (style.id === quoteId ? updatedItem : style));

			setOrderStyles(updatedData);
			setRemovedColorIds([...removedColorIds, itemId]);
		}
	};
	const getSizeWisePrice = (sizeWisePrices, id) => {
		if (sizeWisePrices && sizeWisePrices[id]) {
			return sizeWisePrices[id];
		}
		return '';
	};

	const getSizeWisePoIdentifier = (orderItem, sizeItem) => {
		if (!sizeItem) return '';
		const saved = orderItem?.sizeWisePoItemIdentifier?.[sizeItem.id];
		if (saved !== null && saved !== undefined && saved !== '') return saved;
		return sizeItem?.sku || '';
	};

	const getColorWisePoIdentifier = (orderItem, colorItem) => {
		if (!colorItem) return '';
		const saved = orderItem?.colorWisePoItemIdentifier?.[colorItem.id];
		if (saved !== null && saved !== undefined && saved !== '') return saved;
		return colorItem?.sizeQuantityPairList?.find((s) => s?.sku)?.sku || '';
	};

	const getDesignWisePoIdentifier = (orderItem) => {
		const savedIdentifier = orderItem?.designWisePoItemIdentifier;
		if (savedIdentifier !== null && savedIdentifier !== undefined && savedIdentifier !== '') {
			return savedIdentifier;
		}

		const firstColor = orderItem?.colorWiseSizeQuantityPairList?.[0];
		const firstSku = firstColor?.sizeQuantityPairList?.find((size) => size?.sku)?.sku;
		return firstSku || '';
	};
	const editColor = (color) => {
		setEditColorPopUp(true);
		setIsOverflow(true);
		setColorType(color.colorType);
		setSelectedColorEditName(color.name);
		if (color.colorType === 'SOLID') {
			setSelectedColorEditList([color]);

			//	setSelectedColorEditList(color.compositeColorList);
		} else {
			setSelectedColorEditList(color.compositeColorList);
		}
	};

	const onChangeExtraInfoName = (e, quoteId, colorItemId) => {
		let { value } = e.target;
		const newData = [...stylesData];
		const orderData = newData.find((ite) => ite.id === quoteId);
		if (orderData) {
			const colorItem = orderData.colorWiseSizeQuantityPairList.find((pair) => pair.id === colorItemId);

			if (colorItem) {
				colorItem.sizeQuantityPairList.forEach((item) => {
					item.additionalInfoLabel = value;
				});
			}

			const index = newData.findIndex((ite) => ite.id === quoteId);
			if (index !== -1) {
				newData[index] = orderData;
				setOrderStyles(newData);
			} else {
				printApiError('Order data index not found');
			}
		} else {
			printApiError('Order data not found');
		}
	};

	const onChangeExtraInfoItem = (e, quoteId, colorItemId, item, sizeId) => {
		let { value } = e.target;
		const newData = [...stylesData];
		const orderData = newData.find((ite) => ite.id === quoteId);
		if (orderData) {
			const colorItem = orderData.colorWiseSizeQuantityPairList.find((pair) => pair.id === colorItemId);

			if (colorItem) {
				const sizeItem = colorItem.sizeQuantityPairList.find((item) => item.id === sizeId);

				if (sizeItem) {
					sizeItem.additionalInfo = value;
				}

				const index = newData.findIndex((ite) => ite.id === quoteId);
				if (index !== -1) {
					newData[index] = orderData;
					setOrderStyles(newData);
				} else {
					printApiError('Order data index not found');
				}
			}
		} else {
			printApiError('Order data not found');
		}
	};

	const onChangeItemDescription = (e, quoteId, colorItemId) => {
		let { value } = e.target;
		const newData = [...stylesData];
		const orderData = newData.find((ite) => ite.id === quoteId);
		if (orderData) {
			const colorItem = orderData.colorWiseSizeQuantityPairList.find((pair) => pair.id === colorItemId);

			if (colorItem) {
				colorItem.itemDescription = value;
				const index = newData.findIndex((ite) => ite.id === quoteId);
				if (index !== -1) {
					newData[index] = orderData;
					setOrderStyles(newData);
				} else {
					printApiError('Order data index not found');
				}
			}
		} else {
			printApiError('Order data not found');
		}
	};

	const renderTableHeading = (item, tableHeading) => {
		const shouldReduceLength =
			item.poItemIdentifierType === 'COLORWISE' || item.poItemIdentifierType === 'DESIGNWISE';
		const headingsToRender = shouldReduceLength ? tableHeading : tableHeading.slice(0, -1);
		return headingsToRender.map((size) => <td key={size.id}></td>);
	};

	let tableHeadingBeforSort =
		item?.colorWiseSizeQuantityPairList?.length > 0
			? item?.colorWiseSizeQuantityPairList[0]?.sizeQuantityPairList
			: item?.sizeQuantityPairList;
	let tableHeading = sortBySerial(tableHeadingBeforSort, item.sizeCategorySequenceMap);

	const getIsDisabled = () => {
		if (typeof isDisabled === 'undefined') {
			return !item.isEdit;
		}
		return isDisabled;
	};

	const handleProductRequiredDropdownClick = (event) => {
		if (isFromPoAutomation && !isFinite(Number(item?.productId))) {
			event.preventDefault();
			event.stopPropagation();
			toastError('Please select a product first');
		}
	};

	const getIsDisabledTable = getIsDisabled;
	const displayItemAmount = getDisplayItemAmount(item);

	return (
		<div className="design-quantity-table w-100">
			<div className="design-info-dropdowns">
				<div className="dropdows-row d-flex justify-content-end mb-1">
					<div className="option">
						<div className="dropdown">
							<button
								className="btn btn-default dropdown-toggle gray_dark_02 regular-14"
								type="button"
								data-toggle="dropdown"
								aria-expanded="false"
								disabled={getIsDisabled()}
							>
								Price{' '}
								{item?.buyerQuotationType
									? parseQuoteName(item.buyerQuotationType)
									: 'Quote type'}
								<CommonImage src="/icons/down-arrow.png" alt="down" className="ml-2" />
							</button>
							<ul className="dropdown-menu dropdown-menu-right shadow-4dp">
								{segmentationTypes
									?.filter((item) => item.label !== 'N/A')
									?.map((item, idx) => (
										<li className="px-4 pb-3 pt-3" key={idx}>
											<a
												href={void 0}
												onClick={() => onChangeQuotationType(item, quoteId)}
												className="gray_dark_02"
											>
												{item?.label}
											</a>
										</li>
									))}
							</ul>
						</div>
					</div>
					<div className="option">
						<div className="dropdown">
							<button
								className="btn btn-default dropdown-toggle gray_dark_02 regular-14"
								type="button"
								data-toggle="dropdown"
								aria-expanded="false"
								disabled={getIsDisabled()}
							>
								{poItemIdentifier}{' '}
								{item?.poItemIdentifierType
									? parseQuoteName(item?.poItemIdentifierType)
									: 'PO type'}
								<CommonImage src="/icons/down-arrow.png" alt="down" className="ml-2" />
							</button>
							<ul className="dropdown-menu dropdown-menu-right shadow-4dp">
								{segmentationTypes?.map((item, idx) => (
									<li className="px-4 pb-3 pt-3" key={idx}>
										<a
											href={void 0}
											onClick={() => onChangeSKUType(item, quoteId)}
											className="gray_dark_02"
										>
											{item?.label}
										</a>
									</li>
								))}
							</ul>
						</div>
					</div>
					<div className="option">
						<div className="dropdown">
							<button
								className="btn btn-default dropdown-toggle gray_dark_02 regular-14"
								type="button"
								data-toggle="dropdown"
								aria-expanded="false"
								disabled={getIsDisabled()}
							>
								{item?.priceType || defaultPriceType || 'Incoterms'}
								<CommonImage src="/icons/down-arrow.png" alt="" className="ml-2" />
							</button>
							<ul className="dropdown-menu dropdown-menu-right shadow-4db">
								{incoTerms.map((item) => {
									return (
										<li className="px-4 pb-3 pt-3" key={item.value}>
											<a
												href={void 0}
												onClick={() => onChangePriceType(item.value, quoteId)}
												className="gray_dark_02"
											>
												{item.label}
											</a>
										</li>
									);
								})}
							</ul>
						</div>
					</div>
					{isFromPoAutomation && (
						<div className="option">
							<div
								className="size-category transparent-select"
								onClickCapture={handleProductRequiredDropdownClick}
							>
								<CustomDropdown
									isAddNew={false}
									type="addItem"
									title="Size Category"
									onItemClick={(value) => {
										onItemClick(value);
									}}
									items={sizeCategoryList}
									selectedItem={localSelectedSizeCategory}
									onItemSearch={handleSearch}
									hideSearchBox={true}
									shadowNone={true}
									isDisabled={getIsDisabled()}
								/>
							</div>
						</div>
					)}
				</div>
			</div>
			<div className="design-table d-block ">
				<div
					className={`category-wise-table design-wise-table ${
						isSizeDropdownOpen ? 'overflow-x-none' : ''
					} ${tableHeading && tableHeading?.length ? '' : 'empty-table'}`}
				>
					<table className="table mt-4">
						<thead>
							<tr>
								{(item.poItemIdentifierType === 'COLORWISE' ||
									item.poItemIdentifierType === 'DESIGNWISE') && (
									<th className="color-and-size-column">
										<p className="regular-12 weight-500 col-heading d-flex justify-content-center align-items-center">
											{poItemIdentifier}
										</p>
									</th>
								)}
								<th
									className="color-and-size-column"
									onClick={() => onAddColorModal(quoteId)}
								>
									<p className="title regular-10 gray_dark_02">Color</p>
									{/* {isAddColor && (
										<div ref={colorRef}> */}
									<AddColorModal
										item={item}
										setIsAddSize={setIsAddSize}
										stylesData={stylesData}
										setOrderStyles={setOrderStyles}
										addDesignIndex={addDesignIndex}
										setRemovedColorIds={setRemovedColorIds}
										removedColorIds={removedColorIds}
										editColorPopUp={editColorPopUp}
										setEditColorPopUp={setEditColorPopUp}
										colorType={colorType}
										selectedColorEditList={selectedColorEditList}
										selectedColorEditName={selectedColorEditName}
										setIsOverflow={setIsOverflow}
										disabled={getIsDisabledTable()}
									/>
									{/* </div> */}
									{/* )} */}
									<EditColorModal
										directColorPopUp={directColorPopUp}
										setDirectColorPopUp={setDirectColorPopUp}
										colorType={colorType}
										selectedColorEditList={selectedColorEditList}
										selectedColorEditName={selectedColorEditName}
										setIsOverflow={setIsOverflow}
										disabled={getIsDisabledTable()}
										onUpdateColor={handleColorUpdate}
										isEditColor={true}
										productId={item?.productId}
									/>
								</th>
								{tableHeading?.map((value) => (
									<th
										key={value.id}
										className={
											isFromPoAutomation
												? `size-heading ${!isFinite(Number(value?.id)) ? 'missing-product-size-id' : ''} ${
														!standardSizeList.find((s) => s.id === value.id)
															? 'miss-match-size-category'
															: ''
													}`
												: ''
										}
									>
										<p className="semibold-14 col-heading d-flex justify-content-center align-items-center">
											{value.label}
										</p>
										{isDrafted && item.isEdit && (
											<span
												className="delete-button radius-4 d-flex justify-content-center align-items-center"
												onClick={() => removeSizeColumn(item, value.id, quoteId)}
											>
												<CommonImage
													src="/icons/delete24.svg"
													alt="delete-image"
													width="19"
												/>
											</span>
										)}
										{/* ///proucut size list dropdown */}
										{isFromPoAutomation && isDrafted && item.isEdit && (
											<div
												className="size-dropdown"
												onClickCapture={(event) => {
													handleProductRequiredDropdownClick(event);
													if (!event.defaultPrevented) {
														setIsSizeDropdownOpen(true);
													}
												}}
											>
												<CustomDropdown
													title={value.label || 'Select Size'}
													onItemClick={(cat) =>
														handleSizeColumnUpdate(cat, value.id)
													}
													selectedItem={
														standardSizeList.find((s) => s.id === value.id)
															? value.label
															: ''
													}
													items={standardSizeList
														.filter(
															(s) =>
																s.id === value.id ||
																!tableHeading.some((th) => th.id === s.id)
														)
														.map((s) => ({
															id: s.id,
															value: s.label,
															sizeCategoryId: s.sizeCategoryId
														}))}
												/>
											</div>
										)}
									</th>
								))}

								{item.buyerQuotationType === 'COLORWISE' && (
									<th className="color-and-size-column">
										<p className="semibold-14 col-heading d-flex justify-content-center align-items-center">
											Unit Price($)
										</p>
									</th>
								)}

								<th
									onClick={() =>
										!getIsDisabledTable()
											? isFromPoAutomation && dropDownselectedProductId
												? onAddMissingSizeModal(dropDownselectedProductId, quoteId)
												: onAddSizeModal(quoteId)
											: undefined
									}
									className={`order-size-column ${getIsDisabledTable() ? 'disable' : ''}`}
								>
									<p className="cursor-pointer col-heading w-100 d-flex justify-content-center align-items-center">
										<CommonImage src="/icons/Add.svg" alt="add" />
									</p>
									<p className="title regular-10 gray_dark_02">Size</p>
								</th>
							</tr>
						</thead>
						<tbody>
							{item.poItemIdentifierType === 'SIZEWISE' ? (
								<tr>
									<th className="color-column">
										<div className="color-and-content d-flex justify-content-center align-items-center">
											<p className="text-center semibold-14">{poItemIdentifier}</p>
										</div>
									</th>

									{item.colorWiseSizeQuantityPairList &&
										item.colorWiseSizeQuantityPairList[0]?.sizeQuantityPairList.map(
											(sizeItem) => (
												<td key={sizeItem.id} className="write-sku-column">
													<input
														type="text"
														placeholder={poItemIdentifier}
														onChange={(e) =>
															onChangePoItemIdentifierSizeWise(
																e,
																quoteId,
																sizeItem.id
															)
														}
														disabled={getIsDisabledTable()}
														value={getSizeWisePoIdentifier(item, sizeItem)}
													/>
												</td>
											)
										)}

									<td className="write-sku-column">
										<input disabled type="text" placeholder="--" />
									</td>
								</tr>
							) : (
								''
							)}
							{item.colorWiseSizeQuantityPairList
								? item.colorWiseSizeQuantityPairList?.map((colorItem, index) => (
										<>
											<tr key={colorItem.id}>
												{item.poItemIdentifierType === 'COLORWISE' && (
													<td className="sku-column">
														<input
															type="text"
															placeholder={poItemIdentifier}
															onChange={(e) =>
																onChangePoItemIdentifierColorWise(
																	e,
																	quoteId,
																	colorItem.id
																)
															}
															disabled={getIsDisabledTable()}
															value={getColorWisePoIdentifier(item, colorItem)}
														/>
													</td>
												)}
												{item.poItemIdentifierType === 'DESIGNWISE' && (
													<td className="sku-column">
														{index === 0 ? (
															<input
																type="text"
																placeholder={poItemIdentifier}
																onChange={(e) =>
																	onChangePoItemIdentifierDesignWise(
																		e,
																		quoteId,
																		colorItem.id
																	)
																}
																disabled={getIsDisabledTable()}
																value={getDesignWisePoIdentifier(item)}
															/>
														) : null}
													</td>
												)}
												<th
													className={`color-column ${
														isFromPoAutomation && !colorItem?.name
															? 'authomation-color-missing'
															: ''
													}`}
												>
													<div className="color-and-content d-flex justify-content-start align-items-center">
														<div
															onClick={() => openAdditionlInfo(colorItem)}
															className="arrow cursor-pointer h-100 d-flex justify-content-center align-items-center"
														>
															<CommonImage
																src={
																	isOpenAdditionalInfo(colorItem)
																		? '/icons/down-arrow.png'
																		: '/icons/right-arrow.svg'
																}
																alt=""
															/>
														</div>
														<div className="color-and-name d-flex justify-content-start align-items-center">
															{colorItem?.colorType === 'SOLID' && (
																<Tooltip
																	title={colorItem?.name}
																	placement="top"
																	arrow
																>
																	<div className="multiple-color d-flex">
																		<span
																			className="color-icon"
																			style={{
																				background: colorItem?.hexCode
																			}}
																		></span>
																	</div>
																</Tooltip>
															)}
															{colorItem?.colorType !== 'SOLID' && (
																<>
																	{colorItem?.compositeColorList?.length >
																		0 && (
																		<Tooltip
																			title={colorItem?.name}
																			placement="top"
																			arrow
																		>
																			<div className="multiple-color d-flex">
																				{colorItem?.compositeColorList?.map(
																					(color, index) => (
																						<span
																							key={color.id}
																							className="color-icon"
																							style={{
																								background:
																									color?.hexCode,
																								marginLeft:
																									index >
																										0 &&
																									-15
																							}}
																						></span>
																					)
																				)}
																			</div>
																		</Tooltip>
																	)}

																	{colorItem?.compositeColorList?.length ===
																		0 && (
																		<Tooltip
																			title={colorItem?.name}
																			placement="top"
																			arrow
																		>
																			<span>
																				{getShortName(
																					colorItem?.name,
																					5
																				)}
																			</span>
																		</Tooltip>
																	)}
																</>
															)}

															{item.isEdit &&
																(isDrafted ||
																	isColorWiseRowAllQuantitiesEmpty(
																		colorItem
																	)) && (
																	<div className="color-action d-flex justify-content-start align-items-center">
																		{isFromPoAutomation && isDrafted && (
																			<span
																				onClick={() =>
																					indvisualEditColor(
																						colorItem
																					)
																				}
																				className="cursor-pointer mr-2"
																			>
																				<CommonImage
																					src="/icons/edit-black-20.svg"
																					alt=""
																				/>
																			</span>
																		)}

																		{!isFromPoAutomation && isDrafted && (
																			<span
																				onClick={() =>
																					editColor(colorItem)
																				}
																				className="cursor-pointer mr-2"
																			>
																				<CommonImage
																					src="/icons/edit-black-20.svg"
																					alt=""
																				/>
																			</span>
																		)}

																		<span
																			className="cursor-pointer"
																			onClick={() =>
																				onRemoveColor(
																					colorItem.id,
																					item,
																					quoteId
																				)
																			}
																		>
																			<CommonImage
																				src="/icons/delete_red.svg"
																				alt=""
																			/>
																		</span>
																	</div>
																)}
														</div>
													</div>
												</th>
												{colorItem &&
													colorItem.sizeQuantityPairList.map((style) => (
														<td key={style.id}>
															<input
																onChange={(e) =>
																	onEditChange(
																		e,
																		colorItem.id,
																		quoteId,
																		style.id
																	)
																}
																type="number"
																placeholder="00"
																value={
																	style.quantity === 0
																		? ''
																		: (style.quantity ?? '')
																}
																disabled={getIsDisabledTable()}
															/>
														</td>
													))}
												{item.buyerQuotationType === 'COLORWISE' ? (
													<td>
														<InputWithPrefix>
															<input
																disabled={getIsDisabledTable()}
																type="number"
																onChange={(e) =>
																	onChangeColorWise(
																		e,
																		quoteId,
																		colorItem.id,
																		item
																	)
																}
																value={getColorWisePrice(
																	item.colorWiseBuyerPrice,
																	colorItem.id
																)}
																placeholder="00"
															/>
														</InputWithPrefix>
													</td>
												) : (
													<td>
														<input
															disabled
															type="number"
															value={getTotal(colorItem.sizeQuantityPairList)}
															placeholder="00"
														/>
													</td>
												)}
												{item.buyerQuotationType === 'COLORWISE' && (
													<td>
														<input
															placeholder="00"
															type="number"
															value={getTotal(colorItem.sizeQuantityPairList)}
															disabled
														/>
													</td>
												)}
											</tr>
											{isOpenAdditionalInfo(colorItem) && (
												<>
													<tr className="another-information">
														{(item.poItemIdentifierType === 'COLORWISE' ||
															item.poItemIdentifierType === 'DESIGNWISE') && (
															<td></td>
														)}
														<td>
															<input
																type="text"
																value={
																	colorItem?.sizeQuantityPairList[0]
																		?.additionalInfoLabel
																}
																placeholder="Write a information"
																onChange={(e) =>
																	onChangeExtraInfoName(
																		e,
																		quoteId,
																		colorItem.id
																	)
																}
																disabled={getIsDisabledTable()}
															/>
														</td>
														{colorItem &&
															colorItem.sizeQuantityPairList.map((style) => (
																<td key={style.id}>
																	<input
																		type="text"
																		value={style.additionalInfo}
																		onChange={(e) =>
																			onChangeExtraInfoItem(
																				e,
																				quoteId,
																				colorItem.id,
																				item,
																				style.id
																			)
																		}
																		disabled={getIsDisabledTable()}
																	/>
																</td>
															))}
													</tr>
													<tr className="another-information">
														{(item.poItemIdentifierType === 'COLORWISE' ||
															item.poItemIdentifierType === 'DESIGNWISE') && (
															<td></td>
														)}
														<td>
															<input
																type="text"
																defaultValue="Item Description"
																disabled={true}
															/>
														</td>
														{colorItem &&
															colorItem.sizeQuantityPairList.length > 0 && (
																<td
																	colSpan={
																		colorItem.sizeQuantityPairList.length
																	}
																>
																	<input
																		type="text"
																		placeholder="Description"
																		value={colorItem.itemDescription}
																		onChange={(e) =>
																			onChangeItemDescription(
																				e,
																				quoteId,
																				colorItem.id
																			)
																		}
																		disabled={getIsDisabledTable()}
																	/>
																</td>
															)}
													</tr>
												</>
											)}
										</>
									))
								: ''}

							{/* If the size is SKU */}

							{item.buyerQuotationType === 'SIZEWISE' ? (
								<tr>
									{item.poItemIdentifierType === 'COLORWISE' && <td></td>}
									{item.poItemIdentifierType === 'DESIGNWISE' && <td></td>}

									<th>
										<p className="semibold-14 unit-price-column col-heading d-flex justify-content-center align-items-center">
											Unit price($)
										</p>
									</th>
									{item.colorWiseSizeQuantityPairList &&
										item.colorWiseSizeQuantityPairList[0]?.sizeQuantityPairList.map(
											(style) => (
												<td key={style.id}>
													<InputWithPrefix>
														<input
															type="number"
															placeholder="00"
															onChange={(e) =>
																onChangeSizeWise(e, quoteId, style.id, item)
															}
															value={getSizeWisePrice(
																item.sizeWiseBuyerPrice,
																style.id
															)}
															disabled={getIsDisabledTable()}
														/>
													</InputWithPrefix>
												</td>
											)
										)}
									<td>
										<div className="totol-price-and-qty d-flex align-items-center justify-content-between">
											<p className="qty-number medium-14 gray_dark_01">
												{item?.total ||
													item?.quantity ||
													getItemTotal(item?.colorWiseSizeQuantityPairList)}{' '}
												QTY
											</p>
											<p className="price-number medium-14 gray_dark_01">
												${displayItemAmount.toFixed(2)}
											</p>
										</div>
									</td>
								</tr>
							) : (
								item.buyerQuotationType === 'COLORWISE' && (
									<tr>
										{tableHeading?.map((size) => (
											<td key={size.id}></td>
										))}
										<td></td>
										<td></td>
										{(item.poItemIdentifierType === 'COLORWISE' ||
											item.poItemIdentifierType === 'DESIGNWISE') && <td></td>}
										<td>
											<div className="totol-price-and-qty d-flex align-items-center justify-content-between">
												<p className="qty-number medium-14 gray_dark_01">
													{item?.total ||
														item?.quantity ||
														getItemTotal(
															item?.colorWiseSizeQuantityPairList
														)}{' '}
													QTY
												</p>
												<p className="price-number medium-14 gray_dark_01">
													${displayItemAmount.toFixed(2)}
												</p>
											</div>
										</td>
									</tr>
								)
							)}
							{item.colorWiseSizeQuantityPairList?.length &&
							item.buyerQuotationType === 'DESIGNWISE' ? (
								<tr>
									{renderTableHeading(item, tableHeading)}

									<th>
										<p className="semibold-14 unit-price-column col-heading d-flex justify-content-center align-items-center">
											Unit price($)
										</p>
									</th>

									<td>
										<InputWithPrefix>
											<input
												onChange={(e) => onChangeDesignWise(e, quoteId)}
												type="number"
												placeholder="00"
												disabled={getIsDisabledTable()}
												value={item?.designWiseBuyerPrice}
											/>
										</InputWithPrefix>
									</td>
									{item.colorWiseSizeQuantityPairList.length > 0 &&
									item.colorWiseSizeQuantityPairList[0]?.sizeQuantityPairList?.length ? (
										<td>
											<div className="totol-price-and-qty d-flex align-items-center justify-content-between">
												<p className="qty-number medium-14 gray_dark_01">
													{item?.total ||
														item?.quantity ||
														getItemTotal(
															item?.colorWiseSizeQuantityPairList
														)}{' '}
													QTY
												</p>
												<p className="price-number medium-14 gray_dark_01">
													${displayItemAmount.toFixed(2)}
												</p>
											</div>
										</td>
									) : (
										''
									)}
								</tr>
							) : (
								''
							)}
						</tbody>
					</table>
					<p className="red regular-14">{error}</p>
					{errorDesign.totalError && errorDesign.designId === item.id && (
						<span className="error">{errorDesign.totalError}</span>
					)}
					{isAddSize && (
						<div ref={sizeRef}>
							<AddSizeModal
								item={item}
								setIsAddSize={setIsAddSize}
								stylesData={stylesData}
								setOrderStyles={setOrderStyles}
								addDesignIndex={addDesignIndex}
								isFromCreateOrder={true}
								disabled={getIsDisabledTable()}
							/>
						</div>
					)}
				</div>
			</div>
			{!isFromPoAutomation && <RfqChangeHistory rfqId={item?.id} />}
		</div>
	);
};

export default ColorSizeBreakDownTable;
